import React, { useState } from 'react';
import {
  NamedLink,
  ExternalLink,
  LayoutComposer,
  Heading,
  H1,
  H3,
  H2,
  PrimaryButton,
} from '../../components';
import classNames from 'classnames';

import StaticPage from '../PageBuilder/StaticPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './PricingPage.module.css';
import { Text } from '../PageBuilder/Primitives/Text';
import heroImage from '../../assets/pricingHero.png';
import payIcon from '../../assets/pricingIcon1.png';
import transparencyIcon from '../../assets/pricingIcon2.png';
import receiveIcon from '../../assets/pricingicon3.png';
import legalIcon from '../../assets/pricingicon4.png';
import supportIcon from '../../assets/pricingicon5.png';
import toggleImage from '../../assets/toggleImage.png';
import whatYouGet from '../../assets/whatyouget.png';
import TestimonialSection from './TestimonialSection';
import logo from '../../assets/Logo.png';
import CTASection from '../../components/CTASection/CTASection';
import { ensureCurrentUser } from '../../util/data';
import { connect } from 'react-redux';

const calculateSavings = (propertyValue) => {
  const firstPortion = 500000;
  const firstRate = 0.03;
  const secondRate = 0.02;

  if (propertyValue <= firstPortion) {
    return Math.round(propertyValue * firstRate);
  } else {
    const firstPart = firstPortion * firstRate;
    const secondPart = (propertyValue - firstPortion) * secondRate;
    return Math.round(firstPart + secondPart);
  }
};

const PricingPage = props => {
  const { currentUser } = props;
  const user = ensureCurrentUser(currentUser);
  const userId = user?.id?.uuid;

  const [propertyValue, setPropertyValue] = useState(720200);
  const savings = calculateSavings(propertyValue).toLocaleString();

  const handleSliderChange = e => {
    setPropertyValue(parseInt(e.target.value, 10));
  };

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage
      className={css.root}
      title="Price"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'PricingPage',
        description: 'Any Property Price Page',
        name: 'Price Page',
      }}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {() => (
          <>
            <TopbarContainer />
            <div className={css.content}>
              <div className={css.heroContainer}>
                <div className={css.heroSection}>
                  <div className={css.heroContent}>
                    <H1 className={css.heroTitle}>Pricing that saves you thousands </H1>
                    <Text className={css.subheading}>
                      Pay a small monthly fee to list your home on AnyProperty.
                    </Text>
                    <Text className={css.month}>
                      <span className={css.amount}>$42</span> / month
                    </Text>
                    <a href="/l/new" className={css.getStarted}>
                      Get started now
                    </a>
                  </div>
                  <div className={css.heroImageContainer}>
                    <img src={heroImage} alt="A girl using Laptop" />
                  </div>
                </div>
                <div className={css.gridContainer}>
                  <div className={css.gridItem}>
                    <img src={payIcon} alt="decoration" />
                    <Text className={css.gridItemText}>Pay $0 in commission fees</Text>
                  </div>
                  <div className={css.gridItem}>
                    <img src={transparencyIcon} alt="decoration" />
                    <Text className={css.gridItemText}>
                      Full transparency, always in your control{' '}
                    </Text>
                  </div>
                  <div className={css.gridItem}>
                    <img src={receiveIcon} alt="decoration" />
                    <Text className={css.gridItemText}>Receive offers from verified buyers</Text>
                  </div>
                  <div className={css.gridItem}>
                    <img src={legalIcon} alt="decoration" />
                    <Text className={css.gridItemText}>
                      Get access to legal & purchase agreements{' '}
                    </Text>
                  </div>
                  <div className={css.gridItem}>
                    <img src={supportIcon} alt="decoration" />
                    <Text className={css.gridItemText}>
                      Expert support for whenever you need it{' '}
                    </Text>
                  </div>
                </div>
              </div>
              <div className={css.propertyIt}>
                <div className={css.propertyItContent}>
                  <H3>AnyProperty it.</H3>
                  <div className={css.saveContainer}>
                    <Text className={css.saveText}>You could save</Text>
                    <div className={css.valueContainer}>
                      <H2 className={css.value}>$ {savings} NZD</H2>
                      <Text className={css.valueSubheading}>
                        property valued at an estimated
                        <span> $ {propertyValue.toLocaleString()} NZD</span>
                      </Text>
                    </div>
                    <div className={css.toggleContainer}>
                      {/* <img src={toggleImage} alt='decoration' /> */}
                      <input
                        type="range"
                        min="100000"
                        max="1000000"
                        value={propertyValue}
                        onChange={handleSliderChange} // Updates value on slider change
                        className={css.slider}
                        style={{
                          background: `linear-gradient(to right, var(--colorPrimary) ${((propertyValue -
                            100000) /
                            900000) *
                            100}%, var(--colorBorder) 0)`,
                        }}
                      />
                      <Text className={css.toggleText}>
                        Use the toggle to see what you could save by cutting out agent fees.
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
              <div className={css.whatYouGetContainer}>
                <div className={css.heroSection}>
                  <div className={css.heroContent}>
                    <H2>What you get selling with AnyProperty</H2>
                    <Text className={css.subheading}>
                      List your home in front of thousands of buyers. Get expert help to sell and
                      close. Start conversation that leads to sale as little as 7 days.{' '}
                    </Text>
                    <ul className={css.bulletContainer}>
                      <li className={css.payText}>Pay in $0 in commission fees</li>
                      <li className={css.payText}>Get expert support & legal resources</li>
                      <li className={css.payText}>View and respond to offers within minutes</li>
                    </ul>
                    <a href="/l/new" className={css.getStartedBullet}>
                      Get started
                    </a>
                  </div>
                  <div className={css.heroImageContainer}>
                    <img src={whatYouGet} alt="decoration" />
                  </div>
                </div>
              </div>
              <div className={css.compareSection}>
                <div className={css.compareContainer}>
                  <H2 className={css.compareHeadingTitle}>How do we compare to the competition?</H2>
                  <div className={css.compareDesktop}>
                    <div className={css.compareContentDesktop}>
                      <div className={css.feeContainer}>
                        <Text>Commission Fee</Text>
                        <Text>Listing / Admin Fee</Text>
                      </div>
                      <div className={css.compareGrid}>
                        <div className={(css.compareGridItem, css.compareBackground)}>
                          <Text>AnyProperty</Text>
                          <Text>$0</Text>
                          <Text>$42 per month</Text>
                          <a href="/l/new" className={css.compareGetStart}>
                            Get started now
                          </a>
                        </div>
                        <div className={css.compareGridItem}>
                          <Text>Other market place</Text>
                          <Text>N/A</Text>
                          <Text>$700 - $1,000</Text>
                        </div>
                        <div className={css.compareGridItem}>
                          <Text>Agents</Text>
                          <Text>$20K - $100K</Text>
                          <Text>$400 - $800</Text>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={css.compareMobile}>
                    <div className={css.compareContentMobile}>
                      <div className={css.commissionContainer}>
                        <div className={css.commissionItem}>
                          <div className={css.logo}>
                            <img src={logo} alt="AnyProperty Logo" />
                          </div>
                          <Text className={classNames(css.headingTitle, css.itemWidth, css.border)}>Other</Text>
                          <Text className={classNames(css.headingTitle, css.itemWidth, css.border)}>
                            Agents
                          </Text>
                        </div>
                        <div className={css.commissionItem}>
                          <Text className={css.commissionText}>Commission Fee</Text>
                          <Text className={classNames(css.commissionText, css.border)}></Text>
                          <Text className={classNames(css.commissionText, css.border)}></Text>
                        </div>
                        <div className={classNames(css.commissionItem, css.fee)}>
                          <Text className={css.commissionAmount}>$0</Text>
                          <Text className={classNames(css.commissionContent, css.itemWidth)}>
                            N/A
                          </Text>
                          <Text className={classNames(css.commissionContent, css.itemWidth)}>
                            $20k to $100k
                          </Text>
                        </div>

                        <div className={css.commissionItem}>
                          <Text className={css.commissionText}>Listing / Admin Fee</Text>
                          <Text className={classNames(css.commissionText, css.border)}></Text>
                          <Text className={classNames(css.commissionText, css.border)}></Text>
                        </div>
                        <div className={classNames(css.commissionItem, css.fee)}>
                          <Text className={css.commissionAmount}>
                            $42 <span className={css.perMonth}>per month</span>
                          </Text>
                          <Text className={classNames(css.commissionContent, css.itemWidth)}>
                            $700 to $1,000
                          </Text>
                          <Text className={classNames(css.commissionContent, css.itemWidth)}>
                            $400 to $800
                          </Text>
                        </div>
                        <a href="/l/new" className={classNames(css.mobileButton, css.compareGetStart)}>
                          Get started now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={css.testimonials}>
              <TestimonialSection
                title="What do buyers and sellers say about AnyProperty"
                description="Browse thousands of homes or enter your criteria to find a good fit. Safely view property and make an offer in minutes."
                buttonLabel="Get Started"
              />
            </div>
            {!userId && (
              <div className={css.cta}>
                <CTASection 
                  heading="Join 10K+ sellers and buyers already on AnyProperty"
                  buttonText="Join now"
                  buttonLink="/signup"
                />
              </div>
            )}
            <FooterContainer />
          </>
        )}
      </LayoutComposer>
    </StaticPage>
  );
};

const mapStateToProps = state => {
  return { currentUser: state.user.currentUser };
};


export default connect(mapStateToProps)(PricingPage);
